import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, XCircle, BookOpen } from "lucide-react";

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/cart");
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else if (response.data.status === "expired") {
          setStatus("failed");
        } else {
          if (attempts < maxAttempts) {
            setTimeout(() => setAttempts(a => a + 1), 2000);
          } else {
            setStatus("failed");
          }
        }
      } catch (error) {
        console.error("Status check failed", error);
        if (attempts < maxAttempts) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("failed");
        }
      }
    };

    checkStatus();
  }, [sessionId, token, attempts, navigate]);

  return (
    <div className="min-h-screen pt-24 pb-32 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-lg mx-auto px-6 text-center">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={64} className="mx-auto text-gold animate-spin mb-6" />
            <h1 className="font-heading text-3xl text-[#EAEAEA] mb-4">
              Processing your payment
            </h1>
            <p className="text-[#A1A1AA]">
              Please wait while we confirm your purchase...
            </p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center">
              <CheckCircle size={40} className="text-green-400" />
            </div>
            <h1 className="font-heading text-4xl text-[#EAEAEA] mb-4">
              Payment Successful
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              Your books are now available in your library. 
              Welcome to the journey of coherence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard">
                <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-8 uppercase text-xs tracking-widest btn-shine" data-testid="go-to-library-btn">
                  <BookOpen size={16} className="mr-2" />
                  Go to My Library
                </Button>
              </Link>
              <Link to="/library">
                <Button variant="outline" className="border-white/20 text-[#A1A1AA] hover:border-gold/30 hover:text-gold rounded-none h-14 px-8 uppercase text-xs tracking-widest">
                  Continue Browsing
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="payment-failed">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <XCircle size={40} className="text-red-400" />
            </div>
            <h1 className="font-heading text-4xl text-[#EAEAEA] mb-4">
              Payment Issue
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              We couldn't confirm your payment. If you were charged, 
              please contact us and we'll resolve it immediately.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/cart">
                <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-8 uppercase text-xs tracking-widest">
                  Return to Cart
                </Button>
              </Link>
              <a href="mailto:hello@somalume.org">
                <Button variant="outline" className="border-white/20 text-[#A1A1AA] hover:border-gold/30 hover:text-gold rounded-none h-14 px-8 uppercase text-xs tracking-widest">
                  Contact Support
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
